import React, { useState, useEffect, useRef } from "react";
import { X, Pencil, Clock, Check } from "lucide-react";
import { Switch } from "./ui/Switch";
import TimePickerModal from "./TimePickerModal";
import { cn } from "../lib/utils";
import { toast } from "react-toastify";

interface RegularShift {
  day: string;
  dayIndex: number;
  isWorking: boolean;
  startTime: string;
  endTime: string;
  scheduleId?: number;
}

interface EmployeeRegularShiftsModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeName: string;
  existingShifts?: RegularShift[];
  /** Persist working toggle (calls POST /admin/update-working/{id}) */
  onUpdateWorking: (
    scheduleId: number | undefined,
    isWorking: boolean,
    day: string,
  ) => Promise<void>;
  /** Persist start/end when both are set in 24h "HH:MM" */
  onUpdateTimes: (
    scheduleId: number | undefined,
    startTime24: string,
    endTime24: string,
    day: string,
  ) => Promise<void>;
}

const EmployeeRegularShiftsModal: React.FC<EmployeeRegularShiftsModalProps> = ({
  isOpen,
  onClose,
  employeeName,
  existingShifts,
  onUpdateWorking,
  onUpdateTimes,
}) => {
  const daysOfWeek = [
    { name: "Monday", index: 1 },
    { name: "Tuesday", index: 2 },
    { name: "Wednesday", index: 3 },
    { name: "Thursday", index: 4 },
    { name: "Friday", index: 5 },
    { name: "Saturday", index: 6 },
    { name: "Sunday", index: 0 },
  ];

  const [shifts, setShifts] = useState<RegularShift[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const editSnapshotRef = useRef<RegularShift | null>(null);
  const [busy, setBusy] = useState(false);
  const [activePicker, setActivePicker] = useState<{
    index: number;
    field: "startTime" | "endTime";
  } | null>(null);

  const formatTimeTo24h = (time12: string): string => {
    if (!time12) return "";
    const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return "";
    const [, hours, minutes, period] = match;
    let h = parseInt(hours, 10);
    if (period.toLowerCase() === "pm" && h < 12) h += 12;
    if (period.toLowerCase() === "am" && h === 12) h = 0;
    return `${h.toString().padStart(2, "0")}:${minutes}`;
  };

  const formatTimeTo12h = (time24: string): string => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours, 10);
    if (Number.isNaN(hour)) return "";
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const primaryPurple = "#885ABB";
  const darkPurple = "#402B69";
  const lightPurple = "#E5E7EB";

  useEffect(() => {
    if (isOpen) {
      if (existingShifts && existingShifts.length > 0) {
        setShifts(existingShifts.map((shift) => ({ ...shift })));
      } else {
        setShifts(
          daysOfWeek.map((day) => ({
            day: day.name,
            dayIndex: day.index,
            isWorking: true,
            startTime: "09:00",
            endTime: "18:00",
            scheduleId: undefined,
          })),
        );
      }
      setEditingIndex(null);
      editSnapshotRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingShifts]);

  const revertToggle = (index: number, previous: RegularShift) => {
    setShifts((prev) => prev.map((s, i) => (i === index ? previous : s)));
  };

  const handleToggleWorking = async (index: number) => {
    const shift = shifts[index];
    const previous = { ...shift };
    const isWorking = !shift.isWorking;

    setShifts((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        return {
          ...s,
          isWorking,
          startTime: isWorking ? s.startTime || "09:00" : "",
          endTime: isWorking ? s.endTime || "18:00" : "",
        };
      }),
    );

    setBusy(true);
    try {
      await onUpdateWorking(shift.scheduleId, isWorking, shift.day);
    } catch (e) {
      console.error(e);
      revertToggle(index, previous);
      toast.error("Could not update working status. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleTimeChange = (
    index: number,
    field: "startTime" | "endTime",
    value: string,
  ) => {
    setShifts((prev) =>
      prev.map((shift, i) => (i === index ? { ...shift, [field]: value } : shift)),
    );
  };

  const tryPersistTimes = async (index: number, row: RegularShift) => {
    if (!row.isWorking) {
      toast.error("Cannot save this schedule row.");
      return;
    }
    if (!row.startTime || !row.endTime) {
      toast.error("Please set both start and end times.");
      return;
    }
    if (row.startTime >= row.endTime) {
      toast.error("End time must be after start time.");
      return;
    }
    setBusy(true);
    try {
      await onUpdateTimes(row.scheduleId, row.startTime, row.endTime, row.day);
      setEditingIndex(null);
      editSnapshotRef.current = null;
    } catch (e) {
      console.error(e);
      toast.error("Could not update shift times. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const startEditingRow = (index: number) => {
    const row = shifts[index];
    if (!row.isWorking) return;
    editSnapshotRef.current = { ...row };
    setEditingIndex(index);
  };

  const confirmSaveTimes = () => {
    if (editingIndex === null) return;
    const idx = editingIndex;
    setShifts((prev) => {
      const row = prev[idx];
      queueMicrotask(() => {
        void tryPersistTimes(idx, row);
      });
      return prev;
    });
  };

  const cancelEditingRow = () => {
    if (editingIndex === null) return;
    const snap = editSnapshotRef.current;
    if (snap) {
      setShifts((prev) =>
        prev.map((s, i) => (i === editingIndex ? { ...snap } : s)),
      );
    }
    setEditingIndex(null);
    editSnapshotRef.current = null;
    setActivePicker(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col border border-gray-200">
        <div
          className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0"
          style={{ borderColor: lightPurple }}
        >
          <div>
            <h2 className="text-lg font-semibold" style={{ color: darkPurple }}>
              Employee Regular Shifts
            </h2>
            {employeeName ? (
              <p className="text-sm text-gray-500 mt-0.5">{employeeName}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X
              className="w-5 h-5"
              style={{ color: primaryPurple }}
              strokeWidth={2.5}
            />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ backgroundColor: primaryPurple }}>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-white border-r border-white/20">
                      DAY
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-white border-r border-white/20">
                      IS WORKING
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-white border-r border-white/20">
                      START DATE TIME
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-white border-r border-white/20">
                      END DATE TIME
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-white">
                      ACTION
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((shift: RegularShift, index: number) => {
                    const isEditing = editingIndex === index;
                    return (
                      <tr
                        key={`${shift.day}-${index}`}
                        className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                      >
                        <td
                          className="px-4 py-4 text-sm font-medium"
                          style={{ color: darkPurple }}
                        >
                          {shift.day}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <div className="flex justify-center">
                            <Switch
                              checked={shift.isWorking}
                              disabled={busy}
                              onCheckedChange={() => handleToggleWorking(index)}
                            />
                          </div>
                        </td>

                        <td className="px-4 py-4 text-center">
                          {isEditing && shift.isWorking ? (
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                !busy && setActivePicker({ index, field: "startTime" })
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ")
                                  setActivePicker({ index, field: "startTime" });
                              }}
                              className={cn(
                                "flex items-center justify-between w-32 h-9 mx-auto px-3 border border-gray-200 rounded-md transition-all",
                                busy
                                  ? "opacity-50 cursor-not-allowed"
                                  : "bg-white cursor-pointer hover:border-[#885ABB] hover:shadow-sm",
                              )}
                            >
                              <span
                                className="text-xs font-medium"
                                style={{ color: darkPurple }}
                              >
                                {formatTimeTo12h(shift.startTime) || "—"}
                              </span>
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                            </div>
                          ) : (
                            <span
                              className="text-xs font-medium tabular-nums"
                              style={{
                                color: shift.isWorking ? darkPurple : "#9CA3AF",
                              }}
                            >
                              {shift.isWorking
                                ? formatTimeTo12h(shift.startTime) || "—"
                                : "—"}
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-4 text-center">
                          {isEditing && shift.isWorking ? (
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                !busy && setActivePicker({ index, field: "endTime" })
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ")
                                  setActivePicker({ index, field: "endTime" });
                              }}
                              className={cn(
                                "flex items-center justify-between w-32 h-9 mx-auto px-3 border border-gray-200 rounded-md transition-all",
                                busy
                                  ? "opacity-50 cursor-not-allowed"
                                  : "bg-white cursor-pointer hover:border-[#885ABB] hover:shadow-sm",
                              )}
                            >
                              <span
                                className="text-xs font-medium"
                                style={{ color: darkPurple }}
                              >
                                {formatTimeTo12h(shift.endTime) || "—"}
                              </span>
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                            </div>
                          ) : (
                            <span
                              className="text-xs font-medium tabular-nums"
                              style={{
                                color: shift.isWorking ? darkPurple : "#9CA3AF",
                              }}
                            >
                              {shift.isWorking
                                ? formatTimeTo12h(shift.endTime) || "—"
                                : "—"}
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={confirmSaveTimes}
                                  className="inline-flex items-center justify-center w-9 h-9 rounded-md text-white bg-[#885ABB] hover:bg-[#6E3FA9] disabled:opacity-50 transition-colors"
                                  aria-label="Save times"
                                >
                                  <Check className="w-4 h-4" strokeWidth={2.5} />
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={cancelEditingRow}
                                  className="inline-flex items-center justify-center w-9 h-9 rounded-md text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 transition-colors"
                                  aria-label="Cancel edit"
                                >
                                  <X className="w-4 h-4" strokeWidth={2.5} />
                                </button>
                              </>
                            ) : shift.isWorking &&
                              shift.scheduleId &&
                              editingIndex === null ? (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => startEditingRow(index)}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-md text-white bg-[#885ABB] hover:bg-[#6E3FA9] disabled:opacity-50 transition-colors"
                                aria-label={`Edit ${shift.day} shift`}
                              >
                                <Pencil className="w-4 h-4" strokeWidth={2} />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <TimePickerModal
        isOpen={!!activePicker}
        onClose={() => setActivePicker(null)}
        selectedTime={
          activePicker
            ? formatTimeTo12h(
                shifts[activePicker.index]?.[activePicker.field] || "",
              )
            : ""
        }
        onSelectTime={(time12) => {
          if (!activePicker) return;
          const { index, field } = activePicker;
          handleTimeChange(index, field, formatTimeTo24h(time12));
        }}
      />
    </div>
  );
};

export default EmployeeRegularShiftsModal;
